import { useEffect, useState } from "react";
import { SearchX, StoreIcon } from "lucide-react";

import type { StorefrontResponse } from "@sirdab/returns-shared";
import { Card, EmptyState, Spinner } from "@sirdab/ui";

import { apiClient, ApiProvider, type ApiClient } from "./api";
import { MerchantHeader, OfflineBanner } from "./components";
import { FlowProvider, useFlow } from "./flow";
import { LocaleProvider, useMessages } from "./i18n";
import { matchRoute, screenFor, type RouteMatch } from "./routes";
import { DeepLinkScreen, TrackScreen } from "./screens";
import { ThemeProvider } from "./theme";

/** Current location as a matched route. Re-resolves on back/forward and on `navigate()`. */
function useRoute(): RouteMatch | null {
  const [match, setMatch] = useState<RouteMatch | null>(() =>
    typeof window === "undefined" ? null : matchRoute(window.location.pathname),
  );
  useEffect(() => {
    const onChange = (): void => setMatch(matchRoute(window.location.pathname));
    window.addEventListener("popstate", onChange);
    return () => window.removeEventListener("popstate", onChange);
  }, []);
  return match;
}

/**
 * What we know about the store before the shopper is asked for anything (SAI-1384).
 *
 * The API has always answered this and the portal never asked. So a store that had
 * returns switched off — which is EVERY store the moment it installs the app — walked the
 * shopper through six steps before refusing, and a mistyped slug did the same. The
 * merchant's language, logo, colour and name were also absent from the first screen
 * despite being set, because nothing had read them yet.
 */
type Storefront =
  | { readonly state: "loading" }
  | { readonly state: "ready"; readonly data: StorefrontResponse }
  | { readonly state: "missing" }
  /** The store is real but not taking returns, or Sirdab suspended it. Same screen. */
  | { readonly state: "closed"; readonly data: StorefrontResponse }
  /**
   * We could not reach the API. NOT treated as "closed": telling a shopper the store
   * refuses returns because our own network blinked would be us inventing a policy
   * decision. The flow opens and fails later with a real reason if it fails at all.
   */
  | { readonly state: "unknown" };

function useStorefront(
  merchantSlug: string | null,
  client: ApiClient,
): Storefront {
  const [storefront, setStorefront] = useState<Storefront>(
    merchantSlug === null ? { state: "unknown" } : { state: "loading" },
  );

  useEffect(() => {
    if (merchantSlug === null) return;
    let live = true;
    setStorefront({ state: "loading" });
    void client.getStorefront(merchantSlug).then((result) => {
      if (!live) return;
      if (!result.ok) {
        setStorefront(
          result.kind === "response" && result.status === 404
            ? { state: "missing" }
            : { state: "unknown" },
        );
        return;
      }
      setStorefront(
        result.data.returnsEnabled
          ? { state: "ready", data: result.data }
          : { state: "closed", data: result.data },
      );
    });
    return () => {
      live = false;
    };
  }, [merchantSlug, client]);

  return storefront;
}

/** The store exists but is not taking returns. Its own words, not ours. */
function StoreClosed(): React.JSX.Element {
  const { t } = useMessages();
  return (
    <EmptyState icon={StoreIcon} title={t("portal.closed")} className="py-8" />
  );
}

/**
 * Reading the store, before anything else exists.
 *
 * Deliberately outside every provider: `LocaleProvider` and `ThemeProvider` read the
 * merchant's language and theme when they MOUNT, so mounting them before the answer
 * arrives would leave the page in the wrong language with no logo. Holding them back one
 * beat is what makes the first screen the merchant's own, and it also spares the shopper
 * a flash of unbranded page.
 */
function BootLoading(): React.JSX.Element {
  return (
    <div className="flex min-h-dvh items-center justify-center bg-background">
      <Spinner />
    </div>
  );
}

/** Renders whichever of steps 1-7 the in-memory flow is on. */
function FlowOutlet(): React.JSX.Element {
  const { step } = useFlow();
  const Screen = screenFor(step);
  return <Screen />;
}

/**
 * A track link: the deep-link screen resolves it and shows that return's tracking. If the
 * link is dead it falls back to step 1, and once the shopper moves on from there this is
 * the ordinary flow.
 */
function LinkOutlet({ token }: { token: string }): React.JSX.Element {
  const { step } = useFlow();
  return step === "lookup" ? <DeepLinkScreen token={token} /> : <FlowOutlet />;
}

/**
 * The single column. 390px is the design target; the column is the ceiling, so the layout
 * scales up by getting more margin, not more columns.
 *
 * The page background falls back to the foundation's white unless the merchant set one
 * (`theme/brand.ts`); the flow still sits in one Card, which stays on the foundation's own
 * surface tokens regardless of the page background, so nested content never inherits a
 * gradient.
 */
function Shell({ children }: { children: React.ReactNode }): React.JSX.Element {
  return (
    <div
      className="relative min-h-dvh text-foreground antialiased"
      style={{
        background: "var(--page-background, var(--background))",
        color: "var(--page-ink, var(--foreground))",
      }}
    >
      <OfflineBanner />
      <div className="relative mx-auto w-full max-w-lg px-4 pb-16 sm:px-6">
        <MerchantHeader />
        <Card>
          <main>{children}</main>
        </Card>
      </div>
    </div>
  );
}

/** A URL that is not one of the two routes. */
function NotFound(): React.JSX.Element {
  const { t } = useMessages();
  return (
    <EmptyState
      icon={SearchX}
      title={t("ui.route_not_found")}
      className="py-8"
    />
  );
}

function Routed({
  match,
  storefront,
}: {
  match: RouteMatch | null;
  storefront: Storefront;
}): React.JSX.Element {
  if (match !== null) {
    if (storefront.state === "missing") {
      return (
        <Shell>
          <NotFound />
        </Shell>
      );
    }
    if (storefront.state === "closed") {
      return (
        <Shell>
          <StoreClosed />
        </Shell>
      );
    }
  }

  if (match === null) {
    return (
      <Shell>
        <NotFound />
      </Shell>
    );
  }

  if (match.name === "track") {
    return (
      <Shell>
        <TrackScreen merchantSlug={match.merchantSlug} />
      </Shell>
    );
  }

  if (match.name === "link") {
    // Same provider and same shell as the flow route, because a dead link falls back to
    // step 1 and from there to `FlowOutlet`.
    return (
      <FlowProvider merchantSlug={match.merchantSlug}>
        <Shell>
          <LinkOutlet token={match.token ?? ""} />
        </Shell>
      </FlowProvider>
    );
  }

  return (
    <FlowProvider merchantSlug={match.merchantSlug}>
      <Shell>
        <FlowOutlet />
      </Shell>
    </FlowProvider>
  );
}

/**
 * Provider order matters: theme and locale wrap everything, so a merchant colour and the
 * page direction are established before any screen renders. Nothing below re-derives them.
 */
export function App({
  client = apiClient,
}: {
  /** Test seam, matching `ApiProvider`'s. */
  client?: ApiClient;
} = {}): React.JSX.Element {
  const match = useRoute();
  const storefront = useStorefront(match?.merchantSlug ?? null, client);
  const data =
    storefront.state === "ready" || storefront.state === "closed"
      ? storefront.data
      : null;

  if (match !== null && storefront.state === "loading") return <BootLoading />;

  return (
    <LocaleProvider merchantDefault={data?.localeDefault ?? null}>
      <ThemeProvider initialTheme={data?.theme ?? null}>
        <ApiProvider client={client}>
          <Routed match={match} storefront={storefront} />
        </ApiProvider>
      </ThemeProvider>
    </LocaleProvider>
  );
}

export default App;
